import {FormEvent, useCallback, useEffect, useState} from "react";
import {Alert, Button, Form, Stack} from "react-bootstrap";
import {FormRowControl, LoadingButton} from "zavadil-react-common";
import {EmailUtil, StringUtil} from "zavadil-ts-common";
import {useRestClient} from "../../client/SpmRestClient";
import {useAlerts} from "../../utils/UserAlerts";

export type LoginPageProps = {
	lastLogin: string;
	inProgress: boolean;
	errorMessage: string | null;
	onConfirmed: (login: string, password: string) => any;
}

export default function LoginPage({onConfirmed, lastLogin, inProgress, errorMessage}: LoginPageProps) {
	const restClient = useRestClient();
	const userAlerts = useAlerts();
	const [email, setEmail] = useState<string>(lastLogin);
	const [password, setPassword] = useState<string>('');
	const [valid, setValid] = useState<boolean>(false);
	const [forgottenPassword, setForgottenPassword] = useState<boolean>(false);

	useEffect(() => {
		if (forgottenPassword) {
			setValid(EmailUtil.isValidEmail(email));
		} else {
			setValid(EmailUtil.isValidEmail(email) && StringUtil.notBlank(password));
		}
	}, [email, password, forgottenPassword]);

	const confirm = useCallback(
		() => {
			if (valid) onConfirmed(email, password);
		},
		[email, password, valid, onConfirmed]
	);

	const sendLink = useCallback(
		(e: FormEvent) => {
			e.stopPropagation();
			e.preventDefault();
			if (valid) restClient.tokenManager.accessTokensClient
				.forgottenPassword(email)
				.then(() => userAlerts.info("Odkaz pro přihlášení byl odeslán."))
				.catch((e) => userAlerts.err(e));
		},
		[email, valid, userAlerts, restClient]
	);

	return (
		<div className="d-flex flex-column align-items-center justify-content-center p-2">
			<Form onSubmit={confirm}>
				<Stack gap={3}>
					<FormRowControl
						id="email"
						name="email"
						label="Email"
						type="email"
						value={email}
						onChange={(e) => setEmail(e.target.value)}
					/>
					{
						!forgottenPassword && <FormRowControl
							id="password"
							name="password"
							label="Heslo"
							type="password"
							value={password}
							onChange={(e) => setPassword(e.target.value)}
						/>
					}
					<div className="d-flex flex-column gap-2">
						{
							forgottenPassword ? <>
									<Button
										type="submit"
										disabled={!valid}
										onClick={sendLink}
									>
										Zaslat odkaz pro přihlášení
									</Button>
									<small>
										<Button
											size="sm"
											type="button"
											variant="link"
											onClick={() => setForgottenPassword(false)}
										>
											Přihlásit
										</Button>
									</small>
								</>
								: <>
									<LoadingButton
										type="submit"
										disabled={!valid}
										onClick={confirm}
										loading={inProgress}
									>
										Přihlásit
									</LoadingButton>
									<small>
										<Button
											size="sm"
											type="button"
											variant="link"
											onClick={() => setForgottenPassword(true)}
										>
											Zapomenuté heslo
										</Button>
									</small>
								</>
						}
					</div>
				</Stack>
			</Form>
			{
				errorMessage && <Alert variant="danger">{errorMessage}</Alert>
			}
		</div>
	);
}
